import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Plus, Building2, MapPin, Phone, Trash2, Edit } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BranchManagement() {
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', address: '', contact: '', email: '' });

  useEffect(() => { fetchBranches(); }, []);

  const fetchBranches = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/branches`, { withCredentials: true });
      setBranches(data);
    } catch { toast.error('Failed to load branches'); }
    finally { setLoading(false); }
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', address: '', contact: '', email: '' });
    setDialogOpen(true);
  };

  const openEdit = (b) => {
    setEditing(b.id);
    setForm({ name: b.name, address: b.address, contact: b.contact, email: b.email || '' });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await axios.put(`${API_URL}/api/branches/update/${editing}`, form, { withCredentials: true });
        toast.success('Branch updated');
      } else {
        await axios.post(`${API_URL}/api/branches`, form, { withCredentials: true });
        toast.success('Branch created');
      }
      setDialogOpen(false);
      fetchBranches();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this branch?')) return;
    try {
      await axios.delete(`${API_URL}/api/branches/delete/${id}`, { withCredentials: true });
      toast.success('Branch deleted');
      fetchBranches();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  return (
    <div className="space-y-6" data-testid="branch-management">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2" data-testid="branches-title">Branches</h1>
          <p className="text-slate-400">Manage multi-location books, staff and inventory</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={openCreate} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="add-branch-button">
              <Plus className="h-4 w-4 mr-2" /> New Branch
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-xl">
            <DialogHeader>
              <DialogTitle className="text-white">{editing ? 'Edit Branch' : 'New Branch'}</DialogTitle>
              <DialogDescription className="text-slate-400">Each branch has its own customers, staff and books.</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Branch Name *</Label>
                <Input required data-testid="branch-name-input"
                  value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="bg-slate-800 border-slate-700 text-white" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Address *</Label>
                <Textarea required data-testid="branch-address-input"
                  value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
                  className="bg-slate-800 border-slate-700 text-white" rows={2} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Contact *</Label>
                  <Input required data-testid="branch-contact-input"
                    value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white" />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Email</Label>
                  <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white" />
                </div>
              </div>
              <div className="flex justify-end space-x-2">
                <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)} className="text-slate-300">Cancel</Button>
                <Button type="submit" data-testid="submit-branch-button" className="bg-amber-500 hover:bg-amber-600">
                  {editing ? 'Update' : 'Create'} Branch
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-400">Loading...</div>
      ) : branches.length === 0 ? (
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="py-12 text-center">
            <Building2 className="h-12 w-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400" data-testid="no-branches">No branches yet. Create your first location.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {branches.map((b) => (
            <Card key={b.id} className="bg-slate-900 border-slate-800 hover:-translate-y-1 transition-transform" data-testid="branch-card">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-slate-800 rounded-sm text-amber-500"><Building2 className="h-5 w-5" /></div>
                    <div>
                      <h3 className="text-lg font-semibold text-white">{b.name}</h3>
                      <p className="text-xs text-slate-400">{new Date(b.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(b)}
                      className="text-slate-300 hover:text-white h-8 w-8 p-0" data-testid={`edit-branch-${b.id}`}>
                      <Edit className="h-3 w-3" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(b.id)}
                      className="text-red-500 hover:text-red-400 h-8 w-8 p-0" data-testid={`delete-branch-${b.id}`}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="flex items-start space-x-2 text-slate-300"><MapPin className="h-4 w-4 text-slate-500 mt-0.5 shrink-0" /> <span>{b.address}</span></div>
                <div className="flex items-center space-x-2 text-slate-300"><Phone className="h-4 w-4 text-slate-500" /> <span>{b.contact}</span></div>
                {b.email && <div className="text-slate-400 text-xs pl-6">{b.email}</div>}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
